import {
  Button,
  Classes,
  Dialog,
  FormGroup,
  InputGroup,
  NumericInput,
} from '@blueprintjs/core';
import { useAcceptQuote, useClientEmail } from '@axo/shared/data-access/hooks';
import {
  event_log,
  loan_application,
  loan_quote,
} from '@axo/shared/data-access/types';
import { formatDuration } from '../../../../utils/formatDuration';
import styles from '../Actions.module.css';
import { DataAccessContext } from '@axo/shared/data-access/provider';
import { useContext } from 'react';
import { getLocalPartOfEmail } from '../../../../utils/getLocalPartOfEmail';
import {
  KnownResourceType,
  useEventLogger,
} from '@axo/shared/data-access/event-log';
import { EventCodeACE } from '../../../EventLogWrapper/event-code';
import { useGetQuoteMyPageRanking } from '../../BankPanels/useGetQuoteMyPageRanking';

export function AcceptDialog({
  isOpen,
  onClose,
  application,
  item,
  amount,
  setAmount,
}: {
  isOpen: boolean;
  onClose(): void;
  application: loan_application.LoanApplication;
  item: loan_quote.BankQuote;
  amount: number;
  setAmount: (amount: number) => void;
}) {
  const {
    state: { user },
  } = useContext(DataAccessContext);
  const email = useClientEmail(user.clientID);
  const accept = useAcceptQuote(application, item.ID);
  const getQuoteMyPageRanking = useGetQuoteMyPageRanking(application.ID);

  const isAmountValid =
    !item.LenderMaxPossibleAmount ||
    (amount >= item.Amount && amount <= item.LenderMaxPossibleAmount);

  const appTags: event_log.Resource[] = [
    {
      Type: KnownResourceType.LoanApplication,
      ID: application.ID,
    },
    {
      Type: KnownResourceType.LoanQuote,
      ID: item.ID,
    },
  ];
  const log = useEventLogger(...appTags);
  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title="Bank offer accept"
      icon="tick"
    >
      <div className={styles.dialogBody}>
        You are attempting to manually accept this offer:
        <div className={styles.detailsPanel}>
          <FormGroup
            label="Loan amount"
            helperText={
              !isAmountValid &&
              `Amount must be between ${item.Amount} and ${item.LenderMaxPossibleAmount}`
            }
          >
            <NumericInput
              disabled={!item.LenderMaxPossibleAmount}
              value={amount}
              onValueChange={setAmount}
              max={item.LenderMaxPossibleAmount}
              min={item.Amount}
            />
          </FormGroup>
          <FormGroup label="Repayment period">
            <InputGroup disabled value={formatDuration(item)} />
          </FormGroup>
        </div>
      </div>
      <div className={Classes.DIALOG_FOOTER}>
        <div className={Classes.DIALOG_FOOTER_ACTIONS}>
          <Button aria-label="Close Dialog" onClick={onClose}>
            Close
          </Button>
          <Button
            intent="primary"
            aria-label="Accept Offer"
            onClick={async () => {
              const quoteIndex = (getQuoteMyPageRanking(item.ID)?.value ?? 0) - 1
              await accept.mutateAsync({
                AcceptedBy: email.data ? getLocalPartOfEmail(email.data) : '',
                AcceptedByCustomer: false,
                Amount: amount,
                AcceptableQuoteIndex:
                quoteIndex >= 0 ? quoteIndex : undefined,
              });
              log(EventCodeACE.OfferAccepted);
              onClose();
            }}
            disabled={accept.isLoading || !email.data || !isAmountValid}
          >
            Accept
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
